import { useRef } from "react";
import type { DraggableSyntheticListeners } from "@dnd-kit/core";

/**
 * Click detection on a draggable element.
 *
 * Lifted from bd-admin's `TimelineBlock`, comments and all, because both of them record
 * bugs that shipped:
 *
 * dnd-kit's PointerSensor swallows the synthesized `click` event after a pointer
 * interaction, so a drag's release does not accidentally fire a click. That also kills
 * "pure" clicks where no drag happened — making `onClick` on the draggable element
 * effectively dead. So track pointer start → end ourselves and fire the click when the
 * pointer did not move beyond a small threshold, listening to `pointerup`, which dnd-kit
 * does not suppress.
 *
 * And the second, sharper one: do **not** call `stopPropagation` in the handler. React's
 * `stopPropagation` also calls `nativeEvent.stopPropagation()`, which prevents the
 * underlying `pointerup` from bubbling to dnd-kit's document-level listener. dnd-kit then
 * leaves the PointerSensor in an "armed" state — when a dialog opens and the user next
 * moves the mouse, the sensor activates a drag with no button held and the card follows
 * the cursor.
 */
export function useDragClick(options: {
  enabled: boolean;
  isDragging: boolean;
  onClick: () => void;
  /** Called instead of onClick when the element is not movable and the user tugged it. */
  onBlocked?: () => void;
  dragListeners: DraggableSyntheticListeners;
}) {
  const start = useRef<{ x: number; y: number } | null>(null);

  const onPointerDown = (event: React.PointerEvent): void => {
    start.current = { x: event.clientX, y: event.clientY };

    // A card nobody may move gets pointer capture, so the release is reported here even
    // though the cursor has travelled to another cell — otherwise the tug that deserves an
    // explanation is the one gesture that never gets one. Only when there is no drag to
    // disturb: for a movable card, dnd-kit owns the pointer sequence.
    if (options.onBlocked) {
      try {
        (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
      } catch {
        // Capture is a courtesy; a browser that refuses it still gets the plain click path.
      }
    }

    // dnd-kit's own handler must be forwarded by hand, because ours shadows it.
    options.dragListeners?.onPointerDown?.(event as unknown as PointerEvent);
  };

  const onPointerUp = (event: React.PointerEvent): void => {
    const from = start.current;
    start.current = null;
    if (!from || !options.enabled || options.isDragging) return;

    const dx = Math.abs(event.clientX - from.x);
    const dy = Math.abs(event.clientY - from.y);

    if (dx < 5 && dy < 5) {
      // No stopPropagation — see the comment above. This is not an oversight.
      options.onClick();
      return;
    }
    // Moved far enough to be a drag attempt, but nothing lifted: the card is not movable.
    options.onBlocked?.();
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      options.onClick();
    }
  };

  return { handlers: { onPointerDown, onPointerUp, onKeyDown, tabIndex: 0 } };
}
